<template>
  <div class="cb-wrap">
    <div class="cb-card">
      <div v-if="!error" class="cb-spinner"></div>
      <h2 class="cb-title">{{ error ? 'Sign-in failed' : 'Signing you in…' }}</h2>
      <p class="cb-msg">{{ error || 'Completing secure authentication, please wait.' }}</p>
      <button v-if="error" class="cb-btn" @click="$router.replace({ name: 'LoginPage' })">
        Back to login
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const OAUTH_CLIENT_ID = 'bid-ui-spa';

export default {
  name: 'AuthCallback',
  props: {
    getAccessToken: Function,
    headerVisiblity: Function,
  },
  data() {
    return { error: '' };
  },
  async mounted() {
    await this.handleCallback();
  },
  methods: {
    async handleCallback() {
      const query = this.$route.query;

      // Silent SSO probe came back empty (no shared session) — this is not an
      // error; just send the user to the normal login form.
      if (query.error === 'login_required') {
        this.$router.replace({ name: 'LoginPage' });
        return;
      }

      // Authorization server may report a real error back via the redirect.
      if (query.error) {
        this.error = query.error_description || query.error;
        return;
      }

      const code = query.code;
      const returnedState = query.state;
      const savedState = sessionStorage.getItem('pkce_state');
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

      if (!code) {
        this.error = 'Missing authorization code.';
        return;
      }
      // CSRF protection — the state must match what we generated.
      if (!savedState || returnedState !== savedState) {
        this.error = 'Security check failed (state mismatch). Please try again.';
        return;
      }
      if (!codeVerifier) {
        this.error = 'Missing PKCE verifier. Please start sign-in again.';
        return;
      }

      try {
        const redirectUri = window.location.origin + '/auth/callback';
        const params = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: OAUTH_CLIENT_ID,
          code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier,
        });

        const res = await axios.post(this.$API_URL + '/oauth/token', params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        const data = res.data;

        // One-time PKCE material is no longer needed.
        sessionStorage.removeItem('pkce_state');
        sessionStorage.removeItem('pkce_code_verifier');

        // Store in the same shape the rest of the app expects (see UserLogin.vue),
        // plus the refresh token for silent renewal / logout.
        const userData = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          email: data.user.email,
          roles: data.user.roles,
          is_super_admin_company: data.user.is_super_admin_company,
        };

        // Enrich with profile (name, mobile…) like the password login does.
        try {
          const profileRes = await axios.get(this.$API_URL + '/profile', {
            headers: { Authorization: 'Bearer ' + userData.access_token },
          });
          if (profileRes.data) Object.assign(userData, profileRes.data);
        } catch (e) {
          console.error('Failed to fetch profile after PKCE login', e);
        }

        localStorage.setItem('user-info', JSON.stringify(userData));
        if (this.headerVisiblity) this.headerVisiblity(true);
        this.$router.replace({ name: 'Tenders' });
      } catch (e) {
        const detail = e.response && e.response.data
          ? (e.response.data.error_description || e.response.data.error)
          : null;
        this.error = detail || 'Token exchange failed. Please try signing in again.';
        console.error('PKCE token exchange failed', e);
      }
    },
  },
};
</script>

<style scoped>
.cb-wrap {
  flex: 1;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.cb-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  max-width: 380px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.cb-title {
  margin: 1rem 0 0.4rem;
  font-size: 1.2rem;
  color: #0f172a;
}

.cb-msg {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.cb-spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto;
  border: 4px solid #e2e8f0;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: cbspin 0.9s linear infinite;
}

@keyframes cbspin {
  to { transform: rotate(360deg); }
}

.cb-btn {
  margin-top: 1.25rem;
  padding: 0.6rem 1.4rem;
  border: none;
  border-radius: 8px;
  background: #0ea5e9;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.cb-btn:hover { background: #0284c7; }
</style>
